import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Eye } from "lucide-react";

export default function AdminVerifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/verifications", { params })
      .then(({ data }) => setItems(data))
      .catch((err) => console.warn("verifications load failed", err))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (vid, action) => {
    try {
      await api.post(`/admin/verifications/${vid}/${action}`, { notes: notes[vid] || null });
      toast.success(action === "approve" ? "Seller verified" : "Rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-verifications">
      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            data-testid={`ver-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1 border-2 border-black font-mono-b text-xs font-bold uppercase ${filter === s ? "bg-black text-[#D4FF00]" : "bg-white"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono-b p-6" data-testid="ver-loading">Loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono-b p-8 text-center border-2 border-dashed border-black">No verifications with status "{filter}".</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((v) => (
            <div key={v.verification_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`ver-card-${v.verification_id}`}>
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <div>
                  <p className="font-display text-xl leading-tight">{v.user_name || v.user_email}</p>
                  <p className="font-mono-b text-xs text-black/60">{v.user_email}</p>
                </div>
                <span className={`sticker text-[10px] ${v.status === "approved" ? "" : v.status === "rejected" ? "pink" : "orange"}`}>
                  {v.status.toUpperCase()}
                </span>
              </div>
              <p className="font-mono-b text-xs mt-2">
                <b>{v.id_type?.replace("_", " ").toUpperCase()}</b> · {v.id_number}
              </p>
              <p className="font-mono-b text-[10px] text-black/50 mt-1">Submitted {new Date(v.created_at).toLocaleString()}</p>

              <button
                onClick={() => setExpanded(expanded === v.verification_id ? null : v.verification_id)}
                data-testid={`ver-view-${v.verification_id}`}
                className="mt-3 border-2 border-black bg-white px-2 py-1 text-[11px] font-mono-b font-bold uppercase inline-flex items-center gap-1 hover-nudge"
              >
                <Eye className="w-3 h-3" /> {expanded === v.verification_id ? "HIDE DOCS" : "VIEW DOCS"}
              </button>

              {expanded === v.verification_id && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    ["FRONT", v.id_front_url],
                    ["BACK", v.id_back_url],
                    ["SELFIE", v.selfie_url],
                  ].map(([lbl, url]) => url ? (
                    <a key={lbl} href={url} target="_blank" rel="noreferrer" className="block border-2 border-black">
                      <p className="text-[10px] font-mono-b font-bold uppercase bg-black text-[#D4FF00] px-1">{lbl}</p>
                      <img src={url} alt={lbl} className="w-full aspect-square object-cover" />
                    </a>
                  ) : null)}
                </div>
              )}

              {v.status === "pending" && (
                <>
                  <input
                    placeholder="Notes (optional)"
                    data-testid={`ver-notes-${v.verification_id}`}
                    value={notes[v.verification_id] || ""}
                    onChange={(e) => setNotes({ ...notes, [v.verification_id]: e.target.value })}
                    className="brutal-input w-full mt-3 text-xs"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      data-testid={`ver-approve-${v.verification_id}`}
                      onClick={() => decide(v.verification_id, "approve")}
                      className="flex-1 bg-[#D4FF00] border-2 border-black py-2 font-mono-b font-bold uppercase text-xs hover-nudge"
                    >
                      <Check className="inline w-3 h-3" /> APPROVE
                    </button>
                    <button
                      data-testid={`ver-reject-${v.verification_id}`}
                      onClick={() => decide(v.verification_id, "reject")}
                      className="flex-1 bg-[#FF007F] text-white border-2 border-black py-2 font-mono-b font-bold uppercase text-xs hover-nudge"
                    >
                      <X className="inline w-3 h-3" /> REJECT
                    </button>
                  </div>
                </>
              )}
              {v.notes && v.status !== "pending" && (
                <p className="mt-2 font-mono-b text-[11px] bg-[#F4F4F0] border-2 border-black p-2">Admin notes: {v.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
